// 확장자를 적는 이유: 단위 테스트가 앱 번들 밖(node)에서 이 파일을 그대로 읽는다 — node ESM 은 확장자를 추론하지 않는다
import { httpStatusOf } from '../plugins/http/policy.js'
import { STORE_PICKER_TEXT } from './scopeTreeText.js'

/**
 * **매장 저장이 어디로 가는가 — 한 곳.** 의존성 없는 순수 모듈이다(Vue·스토어·`@/` 별칭 금지).
 *
 * 종전에는 `storeFormMixin.save()` 가 `authId === 'admin'` 일 때만 관리자 매장 엔드포인트
 * (`POST /store/mng`·`PUT /store/mng/{storeCd}`)를 고르고 나머지 역할은 그 화면의 기본 URL 을 불렀다.
 * 그런데 그 엔드포인트의 리소스는 **`admin` 과 `mng` 둘 다**에게 열려 있다
 * (`sys_action` = `StoreAddForMng`·`StoreUpForMng`, `sys_auth_x_resources` = `admin,mng`).
 * 그래서 `mng` 로 사업장 트리에서 매장을 저장하면 **가진 권한을 두고 없는 URL** 로 나가 403 이 되고,
 * 이 앱의 403 은 세션을 끝낸다(강제 로그아웃) — 권한 부족이 아니라 **URL 선택**이 결함이었다.
 *
 * 그러니 묻는 것은 역할 문자열이 아니라 **그 세션이 그 액션을 갖고 있는가**다. 화면은 이미 같은 질문으로
 * 「매장 등록」 버튼을 그린다(`CustTreeView` 의 `checkAuthActionAccess('StoreAddForMng')`) — 버튼과 요청이
 * 같은 근거를 쓰면 보이는 버튼이 곧 부를 수 있는 URL 이다.
 *
 * ## 액션만으로는 모자란다 — 화면이 계약을 소유한다([allowMng])
 *
 * 두 엔드포인트 쌍의 **소속 사업장 계약이 반대**다. `POST /store`·`PUT /store/{cd}`·`PUT /store/info/{cd}`
 * 는 서버가 세션에서 사업장을 파생하고, `POST /store/mng`·`PUT /store/mng/{cd}` 는 **본문의 `cust` 를
 * 그대로 쓴다**(`addMng` 는 그 값을 검증하지 않고 매장과 매장 관리자 계정을 함께 만든다).
 *
 * 그래서 액션만 보고 고르면, 첫 역할이 `custMng` 이면서 `mng` 을 겸직한 계정이 「매장 관리」에서 등록할 때
 * **소속 사업장 칸이 없는 폼**이 `POST /store/mng` 로 나가 사업장 없는 매장이 만들어진다(리얼에 그런 계정이
 * 둘 있다). 관리자 엔드포인트를 쓸 수 있는 화면은 그 칸을 그리는 폼 하나뿐이므로, 「이 화면이 그 계약을
 * 만족하는가」는 화면이 답한다. 액션은 그 위에서 **한 번 더** 좁힌다(계약을 만족해도 권한이 없을 수 있다).
 */

/** 관리자 매장 엔드포인트의 액션 id — 서버 `sys_action.action_id` 와 같은 낱말 */
export const STORE_MNG_ACTION = {
  ADD: 'StoreAddForMng',
  UP: 'StoreUpForMng',
}

/** 관리자 매장 엔드포인트 — 등록은 `POST /store/mng`, 수정은 `PUT /store/mng/{storeCd}` */
export const STORE_MNG_URL = '/store/mng'

/** 화면 문구 한 곳 — 컴포넌트 안에 한글을 다시 적지 않는다(i18n 키가 한국어 문장 자체다) */
export const STORE_SAVE_TEXT = {
  /** 보낼 곳이 없을 때 — 폼이 아직 안 읽혔거나 호출부가 `statu` 를 잘못 넘긴 자리다 */
  noTarget: '저장할 수 없습니다. 화면을 다시 불러와 주세요.',
  /**
   * 상세 조회가 실패했을 때 — 그 화면은 **저장할 수 없는 채로 남는다**(잠금은 조회 성공이 푼다).
   * 말하지 않으면 사람은 잠긴 버튼만 보고 이유를 모른다.
   */
  loadFailed: '매장 정보를 불러오지 못했습니다. 새로고침해 주세요.',
  /**
   * 저장은 성공했는데 그 뒤 화면 갱신이 실패했을 때 — **저장 여부를 흐리지 않는다**. 사람이 다시
   * 저장하지 않도록 「저장은 됐다」를 먼저 말한다.
   */
  afterSaveFailed: '저장은 됐지만 화면을 갱신하지 못했습니다. 새로고침해 주세요.',
  /**
   * 읽기는 됐는데 그 뒤 화면 정리(탭·다이얼로그)가 실패했을 때 — [loadFailed] 와 **다른 말**이다.
   * 그쪽은 「읽지 못해 잠긴 채」를 뜻하므로 같은 문구를 쓰면 사람이 새로고침해도 같은 일이 반복된다.
   */
  afterLoadFailed: '매장 정보는 읽었지만 화면을 여는 데 실패했습니다. 다시 시도해 주세요.',
}

/**
 * 그 매장을 **누가 정했는가** — 못 읽었을 때 할 말이 갈린다([storeReadFailureNotice]).
 *
 * - [PICKED] 목록에서 고른 매장(매장 관리의 「수정」).
 * - [ROUTED] 주소(`?storeCd=`)가 정한 매장 — 관리 매장 목록의 매장명(대행 진입)·북마크·손으로 고친 주소. 매장 관리의 진입과
 *   할인 화면의 주소 매장(`storeOptionResolver` 의 ROUTED)이 함께 쓴다.
 */
export const STORE_ORIGIN = Object.freeze({ PICKED: 'picked', ROUTED: 'routed' })

const LOAD_FAILED_NOTICE = Object.freeze({ type: 'error', message: STORE_SAVE_TEXT.loadFailed })
const ROUTE_REFUSED_NOTICE = Object.freeze({ type: 'warning', message: STORE_PICKER_TEXT.optionRouteFailed })

/** 서버가 그 매장을 **확실히** 주지 않았다고 답한 상태 — 잘못된 요청·권한 밖·없음. 이 밖의 상태는 거절로 읽지 않는다 */
const REFUSAL_STATUSES = Object.freeze([400, 403, 404])

/**
 * 서버가 그 매장을 확실히 주지 않았는가 — 200 인데 그 행이 없음(`error` 없음) 또는 [REFUSAL_STATUSES]. **화이트리스트**다
 * (리뷰 N2): 모르는 실패(408·429 같은 일시적 4xx, 세션 만료 401, 5xx, 전송 실패, 성공 콜백이 던진 코드 오류)를 「없거나
 * 범위 밖」으로 말하면 사람이 멀쩡한 매장을 의심한다.
 */
function refusedByServer(error) {
  return error == null || REFUSAL_STATUSES.includes(httpStatusOf(error))
}

/**
 * **매장을 못 읽었을 때의 안내 — 누가 정했는가 × 왜 못 읽었나** (R-K2 J4 · 리뷰 L2·N2). 판정·문구 한 곳이고 순수다 — 화면은
 * 돌려받은 `{type, message}` 를 그대로 띄운다.
 *
 * - 목록에서 고른 매장: 어떤 실패든 폼이 잠긴 채 남는 이유([STORE_SAVE_TEXT.loadFailed]).
 * - 주소가 정한 매장을 서버가 **확실히 주지 않았다**([refusedByServer] — 빈 행·400·403·404): 「주소의 매장을 열 수 없다 ·
 *   없거나 범위 밖일 수 있다 · 고르세요」. 새로고침해도 같으므로 새로고침을 권하지 않는다. 이 갈래끼리는 **갈라 말하지
 *   않는다**(매장 코드 열거의 신호).
 * - 그 밖의 실패는 전부 일반 오류 안내다 — 일시적 4xx(408·429)·세션 만료(401)·5xx·전송 실패·코드 오류는 새로고침이나 다시
 *   로그인으로 풀릴 수 있고, 「없거나 범위 밖」이라고 말하면 틀린다. 이 실패들은 매장의 있고 없음과 무관하게 나므로 갈라
 *   말해도 열거 신호가 되지 않는다.
 *
 * @param {string} origin [STORE_ORIGIN]
 * @param {*} error 그 실패 — 없으면 「200 인데 그 행이 없음」
 * @returns {{type: string, message: string}}
 */
export function storeReadFailureNotice(origin, error) {
  if (origin === STORE_ORIGIN.ROUTED && refusedByServer(error)) return ROUTE_REFUSED_NOTICE
  return LOAD_FAILED_NOTICE
}

/**
 * 저장 요청 한 건의 목적지. **보낼 곳이 없으면 `null`** 이고, 그때 화면은 아무 요청도 하지 않는다.
 *
 * `null` 이 나오는 경우는 셋뿐이다 — 등록·수정 어느 쪽도 아닌 `statu`(종전 `save()` 도 아무 일을 하지
 * 않았다) · 수정인데 대상 매장이 없는 경우 · 관리자 액션도 없고 화면의 기본 URL 도 없는 경우.
 * 마지막 것은 사업장 트리의 매장 폼이 `url` 을 갖지 않기 때문에 실재한다: 액션이 없는 역할이 그 화면에
 * 닿으면 종전에는 `PUT undefined/{storeCd}` 가 나갔다.
 *
 * @param {object} input
 * @param {?string} input.statu 폼 상태 — `'I'`(등록) · `'U'`(수정)
 * @param {?(number|string)} input.storeCd 수정 대상 매장 코드
 * @param {?boolean} input.allowMng 이 화면이 관리자 매장 엔드포인트의 계약(본문 `cust`)을 만족하는가
 * @param {?boolean} input.hasMngAdd 세션이 [STORE_MNG_ACTION.ADD] 를 갖는가
 * @param {?boolean} input.hasMngUp 세션이 [STORE_MNG_ACTION.UP] 를 갖는가
 * @param {?string} input.baseUrl 그 화면의 매장 CRUD 기본 URL(`/store` · `/store/info`)
 * @returns {?{method: string, url: string}} `method` 는 `mainVueAxios` 의 이름(`add`·`up`)
 */
export function storeSaveTarget(input) {
  const form = input || {}
  const mngAdd = form.allowMng && form.hasMngAdd
  const mngUp = form.allowMng && form.hasMngUp

  if ('I' === form.statu) {
    if (mngAdd) return { method: 'add', url: STORE_MNG_URL }

    return form.baseUrl ? { method: 'add', url: form.baseUrl } : null
  }

  if ('U' === form.statu) {
    if (!form.storeCd) return null
    if (mngUp) return { method: 'up', url: STORE_MNG_URL + '/' + form.storeCd }

    return form.baseUrl ? { method: 'up', url: form.baseUrl + '/' + form.storeCd } : null
  }

  return null
}
